import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getToken } from '../utils/token';
import api from '../api';
import { formatRelativeDate } from '../utils/dateFormat';
import CowLoader from '../components/CowLoader';

export default function MainPage() {
  const navigate = useNavigate();
  const [inviteCode, setInviteCode] = useState('');
  const [vaquitas, setVaquitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [debtSummary, setDebtSummary] = useState({ debes: 0, teDeben: 0 });

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      try {
        const [vaquitasRes] = await Promise.all([api.get('/vaquita/my-vaquitas')]);
        setVaquitas(vaquitasRes.data);

        const userId = parseInt(localStorage.getItem('userId'));
        if (userId) {
          const debtsRes = await api.get(`/personal-debts/user/${userId}`);
          const debts = debtsRes.data.filter(d => d.status !== 'Pagada');
          const debes   = debts.filter(d => d.borrowerId === userId).reduce((s, d) => s + d.amount, 0);
          const teDeben = debts.filter(d => d.lenderId   === userId).reduce((s, d) => s + d.amount, 0);
          setDebtSummary({ debes, teDeben });
        }
      } catch (err) {
        console.error('Error al cargar datos:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleCreateVaquita = () => {
    navigate('/create-vaquita');
  };

  const handleViewVaquitas = () => {
    navigate('/my-vaquitas');
  };

  const handleJoinVaquita = () => {
    if (!inviteCode.trim()) {
      alert('Por favor ingresa un código de invitación');
      return;
    }
    navigate(`/vaquita/preview/${inviteCode.trim()}`);
  };

  const net = (debtSummary.teDeben || 0) - (debtSummary.debes || 0);

  return (
    <div className="main-container">
      {!loading && (
        <div className={`balance-hero ${net < 0 ? 'hero-negative' : net > 0 ? 'hero-positive' : 'hero-neutral'}`}>
          <div className="balance-hero-inner">
            {net === 0 && debtSummary.debes === 0 ? (
              <>
                <span className="hero-icon">✅</span>
                <div className="hero-text">
                  <span className="hero-label">Estado financiero</span>
                  <span className="hero-amount hero-ok">¡Todo al día!</span>
                </div>
              </>
            ) : net < 0 ? (
              <>
                <span className="hero-icon">💸</span>
                <div className="hero-text">
                  <span className="hero-label">Debes en total</span>
                  <span className="hero-amount">${Math.abs(net).toLocaleString('es-CL', {maximumFractionDigits:0})}</span>
                </div>
              </>
            ) : (
              <>
                <span className="hero-icon">💰</span>
                <div className="hero-text">
                  <span className="hero-label">Te deben en total</span>
                  <span className="hero-amount">${net.toLocaleString('es-CL', {maximumFractionDigits:0})}</span>
                </div>
              </>
            )}
            {debtSummary.debes > 0 && debtSummary.teDeben > 0 && (
              <div className="hero-breakdown">
                <span className="breakdown-item neg">Debes ${debtSummary.debes.toLocaleString('es-CL', {maximumFractionDigits:0})}</span>
                <span className="breakdown-sep">·</span>
                <span className="breakdown-item pos">Te deben ${debtSummary.teDeben.toLocaleString('es-CL', {maximumFractionDigits:0})}</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="content-grid">
        <div className="left-column">
          <div className="welcome-card">
            <h2 className="welcome-title">🐄 Bienvenido a VaquitApp 🐄</h2>

            <div className="actions-container">
              <section className="join-section">
                <h3 className="section-title">¿Tienes un código de invitación?</h3>
                <div className="join-form">
                  <input
                    type="text"
                    placeholder="Ingresa el código aquí"
                    value={inviteCode}
                    onChange={(e) => setInviteCode(e.target.value)}
                    className="join-input"
                  />
                  <button onClick={handleJoinVaquita} className="join-button">
                    Unirme
                  </button>
                </div>
              </section>

              <div className="divider">o</div>

              <section className="create-section">
                <h3 className="section-title">Crear una nueva vaquita</h3>
                <button onClick={handleCreateVaquita} className="create-button">
                  Crear Vaquita 🐮
                </button>
              </section>
            </div>
          </div>
        </div>

        <div className="right-column">
          <div className="vaquitas-card">
            <div className="vaquitas-header">
              <h3 className="section-title">Mis Vaquitas</h3>
              <button onClick={handleViewVaquitas} className="view-details-button">
                Ver todos los detalles →
              </button>
            </div>


            <div className="vaquitas-list">
              {loading ? (
                <CowLoader text="Cargando..." />
              ) : vaquitas.length === 0 ? (
                <div className="empty-state">
                  <p>No tienes vaquitas activas</p>
                  <p className="suggestion">¡Crea una o únete a una existente!</p>
                </div>
              ) : (
                vaquitas
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .map((vaquita) => (
                    <div 
                      key={vaquita.id} 
                      className="vaquita-item"
                      onClick={() => navigate(`/vaquita/code/${vaquita.inviteCode}`)}
                    >
                      <div className="vaquita-info">
                        <h4 className="vaquita-name">
                          <span>🐮</span>
                          {vaquita.name}
                          {vaquita.creatorUserId === vaquita.currentUserId && (
                            <span className="admin-badge">Admin</span>
                          )}
                        </h4>
                        <div className="vaquita-details">
                          <span className="vaquita-code">
                            Código: {vaquita.inviteCode}
                          </span>
                          {vaquita.balance !== 0 && (
                            <span className={`balance-badge ${vaquita.balance < 0 ? 'negative' : 'positive'}`}>
                              {vaquita.balance < 0 ? '-' : ''}${Math.abs(vaquita.balance).toLocaleString('es-CL', {maximumFractionDigits:0})} {vaquita.balance < 0 ? '👎' : '👍'}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="vaquita-meta">
                        <span className="vaquita-date">
                          {formatRelativeDate(vaquita.createdAt)}
                        </span>
                        <span className="view-arrow">→</span>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .main-container {
          min-height: 100vh;
          padding: 1rem;
          background: var(--bg-primary);
        }

        .balance-hero {
          max-width: 1400px;
          margin: 0 auto 1.25rem;
          border-radius: 14px;
          padding: 1.1rem 1.4rem;
        }

        .hero-negative {
          background: linear-gradient(135deg, rgba(220,53,69,0.15), rgba(220,53,69,0.08));
          border: 1.5px solid rgba(220,53,69,0.35);
        }

        .hero-positive {
          background: linear-gradient(135deg, rgba(40,167,69,0.15), rgba(40,167,69,0.08));
          border: 1.5px solid rgba(40,167,69,0.35);
        }

        .hero-neutral {
          background: var(--bg-secondary);
          border: 1.5px solid var(--border-color);
        }

        .hero-ok {
          color: var(--success-color, #28a745) !important;
          font-size: 1.5rem !important;
        }

        .balance-hero-inner {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }

        .hero-icon { font-size: 2rem; line-height: 1; }

        .hero-text {
          display: flex;
          flex-direction: column;
          gap: 0.1rem;
          flex: 1;
        }

        .hero-label {
          font-size: 0.78rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          color: var(--text-muted);
        }

        .hero-amount {
          font-size: 1.8rem;
          font-weight: 800;
          line-height: 1.1;
        }

        .hero-negative .hero-amount { color: var(--danger-color); }
        .hero-positive .hero-amount { color: var(--success-color, #28a745); }

        .hero-breakdown {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.82rem;
          color: var(--text-muted);
          width: 100%;
          padding-left: calc(2rem + 1rem);
        }

        .breakdown-item.neg { color: var(--danger-color); font-weight: 600; }
        .breakdown-item.pos { color: var(--success-color, #28a745); font-weight: 600; }
        .breakdown-sep { color: var(--text-muted); }

        .content-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1rem;
          max-width: 1400px;
          margin: 0 auto;
        }

        @media (min-width: 900px) {
          .main-container { padding: 2rem; }
          .content-grid { grid-template-columns: 1fr 1fr; gap: 2rem; }
        }

        @media (max-width: 768px) {
          .left-column { display: none; }
        }

        .welcome-card, .vaquitas-card {
          background: var(--bg-secondary);
          border-radius: 12px;
          padding: 1.2rem;
          box-shadow: 0 4px 12px var(--shadow-color);
          height: fit-content;
        }

        @media (min-width: 900px) {
          .welcome-card, .vaquitas-card { padding: 2.5rem; }
        }

        .welcome-title {
          color: var(--text-primary);
          font-size: 1.4rem;
          margin-bottom: 1.5rem;
          text-align: center;
          word-break: break-word;
        }

        @media (min-width: 900px) {
          .welcome-title { font-size: 2rem; margin-bottom: 2.5rem; }
        }

        .section-title {
          color: var(--text-primary);
          font-size: 1.3rem;
          margin-bottom: 1.5rem;
        }

        .actions-container {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .join-section, .create-section {
          background: var(--bg-primary);
          padding: 1rem;
          border-radius: 12px;
        }

        @media (min-width: 768px) {
          .join-section, .create-section { padding: 2rem; }
        }

        .join-form {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        @media (min-width: 500px) {
          .join-form { flex-direction: row; }
        }

        .join-input {
          flex: 1;
          padding: 0.8rem 1.2rem;
          border: 2px solid var(--border-color);
          border-radius: 8px;
          font-size: 1rem;
          background: var(--bg-secondary);
          color: var(--text-primary);
          transition: all 0.2s ease;
          text-transform: none;
        }

        .join-input:focus {
          outline: none;
          border-color: var(--accent-color);
          box-shadow: 0 0 0 3px var(--accent-color-light);
        }

        .divider {
          color: var(--text-muted);
          font-size: 1.1rem;
          margin: 1rem 0;
          display: flex;
          align-items: center;
          gap: 1rem;
          text-align: center;
        }

        .divider::before,
        .divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: var(--border-color);
        }

        .create-button,
        .join-button {
          padding: 1rem 2rem;
          border: none;
          border-radius: 8px;
          font-size: 1.1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          background: var(--accent-color);
          color: white;
          box-shadow: 0 2px 4px var(--shadow-color);
        }

        .create-button {
          width: 100%;
        }

        .create-button:hover,
        .join-button:hover {
          background: var(--accent-hover);
          transform: translateY(-2px);
          box-shadow: 0 4px 8px var(--shadow-color);
        }

        .vaquitas-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .debt-summary {
          display: flex;
          gap: 0.75rem;
          margin-bottom: 1.5rem;
        }

        .debt-summary-item {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0.6rem 1rem;
          border-radius: 8px;
          gap: 0.1rem;
        }

        .debt-summary-item.negative {
          background: rgba(220, 53, 69, 0.12);
          border: 1px solid rgba(220, 53, 69, 0.3);
        }

        .debt-summary-item.positive {
          background: rgba(40, 167, 69, 0.12);
          border: 1px solid rgba(40, 167, 69, 0.3);
        }

        .debt-label {
          font-size: 0.75rem;
          color: var(--text-muted);
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .debt-summary-item.negative .debt-amount {
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--danger-color);
        }

        .debt-summary-item.positive .debt-amount {
          font-size: 1.1rem;
          font-weight: 700;
          color: var(--success-color);
        }

        .view-details-button {
          background: none;
          border: none;
          color: var(--accent-color);
          font-size: 1rem;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          transition: all 0.2s ease;
        }

        .view-details-button:hover {
          background: var(--bg-primary);
          transform: translateX(4px);
        }

        .vaquitas-list {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .vaquita-item {
          background: var(--bg-primary);
          padding: 1.5rem;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .vaquita-item:hover {
          transform: translateX(4px);
          background: var(--bg-hover);
        }

        .vaquita-info {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .vaquita-name {
          color: var(--text-primary);
          font-size: 1.1rem;
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .admin-badge {
          background: var(--accent-color);
          color: white;
          padding: 0.2rem 0.5rem;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: normal;
          margin-left: 0.5rem;
        }

        .vaquita-code {
          color: var(--text-muted);
          font-size: 0.9rem;
          font-family: monospace;
        }

        .vaquita-meta {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .vaquita-date {
          color: var(--text-muted);
          font-size: 0.9rem;
        }

        .view-arrow {
          color: var(--accent-color);
          font-size: 1.2rem;
          transition: transform 0.2s ease;
        }

        .vaquita-item:hover .view-arrow {
          transform: translateX(4px);
        }

        .loading-state {
          color: var(--text-muted);
          text-align: center;
          padding: 2rem;
        }

        .empty-state {
          text-align: center;
          padding: 2rem;
          color: var(--text-muted);
        }

        .suggestion {
          margin-top: 0.5rem;
          font-size: 0.9rem;
          color: var(--text-secondary);
        }

        .vaquita-details {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .balance-badge {
          font-size: 0.7rem;
          padding: 0.2rem 0.5rem;
          border-radius: 12px;
          display: inline-block;
          width: fit-content;
        }

        .balance-badge.negative {
          background: var(--danger-color);
          color: white;
        }

        .balance-badge.positive {
          background: var(--success-color);
          color: white;
        }

      `}</style>
    </div>
  );
}

